// Active partner-context detection (#1315).
//
// A partner enters a CLIENT tenant's context via the partner portal
// (ClientDetailPage / ClientManagementPage / PartnerPortalPage), which stashes
// the partner's own token in `partnerOriginalToken` and sets `partnerContextToken`
// as the scoped session token. The Sidebar breadcrumb ("← Partner Portal")
// clears both on exit, and logout clears them too. So the presence of
// `partnerContextToken` is the reliable "currently VIEWING as a partner" signal.
//
// This is deliberately distinct from `useMyTenant().isPartner`, which is mere
// partner-ACCOUNT membership (identity, orthogonal to the active view). Gating
// the dashboard's partner layout on `isPartner` routed partner-account Tenant
// Admins running their OWN org into the sparse approvals-only partner variant,
// hiding all their PM data (#1315).
export function isInPartnerContext(): boolean {
  if (typeof window === 'undefined') return false;
  const ctx = window.localStorage.getItem('partnerContextToken');
  if (ctx == null || ctx.length === 0) return false;
  // Self-validate: a genuine partner-context session has blueprint_token SET to
  // the scoped context token (ClientDetailPage/ClientManagementPage/Portal set
  // both together on entry). A normal re-auth (login/register/OAuth/magic-link)
  // overwrites blueprint_token but does NOT clear partnerContextToken, so a
  // stale key can linger; requiring equality rejects that stale key and avoids
  // re-routing a freshly re-authenticated user into the sparse partner variant
  // (#1315 adversarial review). Broader stale-key cleanup on re-auth (also
  // affects the Sidebar breadcrumb) is tracked in #1318.
  return window.localStorage.getItem('blueprint_token') === ctx;
}

// ── Enter / exit partner context (#1330) ────────────────────────────────────
//
// The partner-switch flow used to write `localStorage.blueprint_token` DIRECTLY
// and client-side navigate, leaving AuthProvider's in-memory `token` state
// (what `useAuth().token` returns) on the partner's ORIGINAL token. Every
// dashboard fetch sends `useAuth().token`, so `/overview` scoped to the
// partner's own org instead of the client — only a full page reload (which
// re-inits AuthProvider from localStorage) made it correct. These helpers route
// the token swap through `setSession`, keeping localStorage AND React state in
// lockstep so the correct scoped token is used WITHOUT a reload.

/** Structural shape of AuthContext's User (not exported from the context). */
export interface PartnerCtxUser {
  id: string;
  email: string;
  name: string;
}
type SetSession = (token: string, user: PartnerCtxUser) => void;

export interface PartnerContextSwitch {
  /** The client-scoped session token returned by /partner/clients/:id/switch. */
  contextToken: string;
  partnerAccountId: string;
  /** Where the "← Partner Portal" breadcrumb returns to. */
  returnPath: string;
}

/**
 * Enter partner context: stash the partner's own token + context metadata, then
 * sync the scoped context token into BOTH localStorage and AuthProvider state
 * via `setSession` — so `useAuth().token` (and therefore every dashboard fetch)
 * uses the client-scoped token without needing a full page reload.
 */
export function enterPartnerContext(
  sw: PartnerContextSwitch,
  user: PartnerCtxUser,
  setSession: SetSession,
): void {
  // Capture the partner's own token BEFORE setSession overwrites blueprint_token.
  const originalToken = window.localStorage.getItem('blueprint_token');
  if (originalToken) window.localStorage.setItem('partnerOriginalToken', originalToken);
  window.localStorage.setItem('partnerContextToken', sw.contextToken);
  window.localStorage.setItem('partnerReturnPath', sw.returnPath);
  window.localStorage.setItem('partnerAccountId', sw.partnerAccountId);
  // Syncs blueprint_token + the in-memory React token state (the whole point).
  setSession(sw.contextToken, user);
}

export interface PartnerContextExit {
  /** Path to navigate back to. */
  returnPath: string;
  /** True when the partner's own token was restored via setSession. When false
   *  (no stashed original token — should not happen for a valid session) the
   *  caller MUST force a clean logout rather than leave the client-scoped token
   *  active (#1330 review M1). */
  restored: boolean;
}

/**
 * Exit partner context: restore the partner's own token via `setSession` and
 * clear the context keys.
 */
export function exitPartnerContext(user: PartnerCtxUser, setSession: SetSession): PartnerContextExit {
  const originalToken = window.localStorage.getItem('partnerOriginalToken');
  const returnPath = window.localStorage.getItem('partnerReturnPath') || '/partner';
  window.localStorage.removeItem('partnerContextToken');
  window.localStorage.removeItem('partnerOriginalToken');
  window.localStorage.removeItem('partnerReturnPath');
  window.localStorage.removeItem('partnerAccountId');
  if (!originalToken) return { returnPath, restored: false };
  setSession(originalToken, user);
  return { returnPath, restored: true };
}
